import type { CampaignType } from '../../../types/loyaltyMarketing'
import { SMS_CAMPAIGNS_ENABLED } from './featureFlags'

// Pre-launch validation for the campaign builder (spec §4): name, subject,
// body non-empty; cta_url present when cta_text is set; ≥1 segment when the
// audience is segment-based; from_email valid when set. Pure — no React.

// Pragmatic client-side check — the server takes any well-formed address, but
// SendGrid rejects unverified senders at send time.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Email campaigns send from the store's sending domain: only the part before
// the @ may change per campaign (the domain is refused otherwise).
export const SENDER_LOCAL_PART_RE = /^[A-Za-z0-9._+-]+$/

// '' when the address has no domain.
export const emailDomain = (email: string): string => {
  const value = email.trim()
  const at = value.lastIndexOf('@')
  return at > 0 ? value.slice(at + 1) : ''
}

export const emailLocalPart = (email: string): string => {
  const value = email.trim()
  const at = value.lastIndexOf('@')
  return at >= 0 ? value.slice(0, at) : value
}

// True unless a sender domain applies and the edited part before @ is not
// letters, digits or . _ + - (a blank from email = the store sender).
export const senderLocalPartValid = (
  fromEmail: string,
  senderDomain: string,
): boolean =>
  !senderDomain ||
  !fromEmail.trim() ||
  SENDER_LOCAL_PART_RE.test(emailLocalPart(fromEmail))

const HTML_TAG_RE = /<[^>]*>/g
const NBSP_RE = /&nbsp;|&#160;/g
const IMG_TAG_RE = /<img\b/i

// Quill reports an empty document as "<p><br></p>" (sometimes with &nbsp;
// padding); treat any markup with no visible text as empty. An image is
// visible content even though it contributes no text.
export const isBodyEmpty = (html: string): boolean =>
  !IMG_TAG_RE.test(html) &&
  html.replace(HTML_TAG_RE, '').replace(NBSP_RE, ' ').trim() === ''

export interface CampaignChecklistInput {
  // null = no type chosen yet (new campaign).
  type: CampaignType | null
  isFlow: boolean
  // Campaign edits never touch scheduling (managed from the list), so a
  // hydrated past scheduled_at must not block saving content changes.
  isEditingCampaign: boolean
  name: string
  subject: string
  bodyHtml: string
  smsBody: string
  smsValid: boolean
  ctaText: string
  ctaUrl: string
  fromEmail: string
  // The store sender's domain for Email-type campaigns ('' = none): the from
  // email is then edited before the @ only.
  senderDomain?: string
  audienceMode: 'all' | 'segment' | 'ids'
  segmentCount: number
  customerCount: number
  scheduleMode: 'now' | 'fixed' | 'smart'
  scheduledAtValid: boolean
  endsAtValid: boolean
  delayValid: boolean
  // Block emails (content_mode 'blocks'). When present the layout body / CTA
  // rows are replaced by the block rows: template chosen, ≥1 block, and two
  // non-blocking warnings (unsubscribe link, image alt text).
  blocks?: {
    templateKey: string | null
    blockCount: number
    compiledHtml: string
    missingAltCount: number
    // Product blocks that resolve to no products with the LOADED catalog
    // (blocking: the email would send them empty).
    emptyProductBlocks?: number
    // WHY the store catalog can't fill product blocks, when known:
    // 'unsupported' = the store type has no product adapter (the catalog
    // deliberately degrades to empty); 'no-products' = the catalog loaded
    // but the store has no published products. Refines the emptyProducts
    // row's label only.
    emptyProductReason?: 'unsupported' | 'no-products'
    // The subject / preview / blocks use {{coupon}}.
    usesCouponToken?: boolean
    // The doc's Coupon block code (what {{coupon}} is filled with).
    couponCode?: string
    // The doc contains a Cart items block.
    hasCartBlock?: boolean
  }
}

export interface CampaignCheck {
  key: string
  label: string
  ok: boolean
  // false = a warning: shown amber when not ok but never blocks Launch.
  // Absent = blocking.
  blocking?: boolean
}

const UNSUBSCRIBE_TOKEN = '{{unsubscribe_url}}'

// The mockup's "Pre-launch Checklist" rows, in order. Every row must be ok
// before Launch / Activate is enabled.
export const buildCampaignChecks = (
  input: CampaignChecklistInput,
): CampaignCheck[] => {
  const isSms = input.type === 'sms'
  const fromEmailValid =
    (!input.fromEmail.trim() || EMAIL_RE.test(input.fromEmail.trim())) &&
    senderLocalPartValid(input.fromEmail, input.senderDomain ?? '')
  const audienceOk =
    input.isFlow ||
    input.audienceMode === 'all' ||
    (input.audienceMode === 'segment' && input.segmentCount > 0) ||
    (input.audienceMode === 'ids' && input.customerCount > 0)
  const blocks = isSms ? undefined : input.blocks
  const contentOk = isSms
    ? input.smsValid
    : blocks
      ? Boolean(input.subject.trim())
      : Boolean(input.subject.trim()) && !isBodyEmpty(input.bodyHtml)
  const ctaOk = !input.ctaText.trim() || Boolean(input.ctaUrl.trim())
  const scheduleOk = input.isFlow
    ? input.delayValid
    : input.isEditingCampaign ||
      // 'smart' needs no timestamp from the user — the server picks each
      // recipient's hour within 24h of launch.
      input.scheduleMode !== 'fixed' ||
      input.scheduledAtValid

  const checks: CampaignCheck[] = [
    { key: 'type', label: 'Campaign type selected', ok: input.type !== null },
  ]
  // Temporary SMS kill switch: an SMS record can never pass the checklist.
  if (isSms && !SMS_CAMPAIGNS_ENABLED)
    checks.push({
      key: 'smsUnavailable',
      label: 'SMS campaigns are temporarily unavailable',
      ok: false,
    })
  if (blocks)
    checks.push({
      key: 'template',
      label: 'Template chosen',
      ok: Boolean(blocks.templateKey),
    })
  checks.push(
    {
      key: 'name',
      label: 'Campaign name entered',
      ok: Boolean(input.name.trim()),
    },
    {
      key: 'audience',
      label: input.isFlow ? 'Audience — auto-targeted' : 'Audience defined',
      ok: audienceOk,
    },
    {
      key: 'content',
      label: isSms
        ? 'SMS message written'
        : blocks
          ? 'Subject line written'
          : 'Subject and body written',
      ok: contentOk,
    },
  )
  if (blocks) {
    checks.push({
      key: 'blocks',
      label:
        blocks.blockCount > 0
          ? 'Email has content blocks'
          : 'Add at least one block',
      ok: blocks.blockCount > 0,
    })
  } else if (!isSms) {
    checks.push({
      key: 'cta',
      label: input.ctaText.trim() ? 'Button link set' : 'Button (optional)',
      ok: ctaOk,
    })
  }
  checks.push(
    { key: 'sender', label: 'From email valid', ok: fromEmailValid },
    {
      key: 'schedule',
      label: 'Send time set',
      ok: scheduleOk && input.endsAtValid,
    },
  )
  // Smart send time is no longer offered: an unlaunched draft still on it
  // must pick another mode before it can launch.
  if (
    blocks &&
    input.scheduleMode === 'smart' &&
    !input.isFlow &&
    !input.isEditingCampaign
  )
    checks.push({
      key: 'smartSend',
      label:
        'Choose Send immediately or Schedule for later — smart send time is no longer available',
      ok: false,
    })
  if (blocks) {
    const hasUnsub = blocks.compiledHtml.includes(UNSUBSCRIBE_TOKEN)
    checks.push(
      {
        key: 'unsubscribe',
        label: hasUnsub
          ? 'Footer has an unsubscribe link'
          : 'No unsubscribe link — the server will append one',
        ok: hasUnsub,
        blocking: false,
      },
      {
        key: 'altText',
        label:
          blocks.missingAltCount > 0
            ? `${blocks.missingAltCount} image${blocks.missingAltCount === 1 ? '' : 's'} missing alt text`
            : 'Images have alt text',
        ok: blocks.missingAltCount === 0,
        blocking: false,
      },
    )
    if ((blocks.emptyProductBlocks ?? 0) > 0)
      checks.push({
        key: 'emptyProducts',
        label:
          blocks.emptyProductReason === 'unsupported'
            ? 'A product block can’t be filled — this store type doesn’t support product blocks'
            : blocks.emptyProductReason === 'no-products'
              ? 'A product block has no products to show — no published products found; publish products or pick them manually'
              : 'A product block has no products to show',
        ok: false,
      })
    // {{coupon}} is filled from the campaign's coupon code, which comes from
    // the doc's Coupon block — without one it sends blank.
    if (blocks.usesCouponToken && !blocks.couponCode?.trim())
      checks.push({
        key: 'couponToken',
        label: 'Add a Coupon block to use {{coupon}}',
        ok: false,
      })
    if (blocks.hasCartBlock && input.type !== 'abandoned_cart')
      checks.push({
        key: 'cartItems',
        label:
          'Cart items only appear in abandoned-cart emails — regular campaigns show the View your cart button',
        ok: false,
        blocking: false,
      })
  }
  return checks
}

const isBlocking = (check: CampaignCheck) => check.blocking !== false

// Every BLOCKING row is ok (warnings never hold Launch back).
export const checksPass = (checks: CampaignCheck[]): boolean =>
  checks.every((check) => check.ok || !isBlocking(check))

// "Save changes" on a non-draft (launched) record: the old page's gate —
// content, CTA, audience, sender (+ delay for flows). Template, name, the
// launched schedule / end date and warnings don't hold an edit back.
const SAVE_CHANGES_KEYS = [
  'content',
  'cta',
  'blocks',
  'audience',
  'sender',
  'emptyProducts',
  'couponToken',
]

export const saveChangesReady = (
  checks: CampaignCheck[],
  isFlow: boolean,
): boolean =>
  checks.every(
    (c) =>
      c.ok ||
      !(SAVE_CHANGES_KEYS.includes(c.key) || (isFlow && c.key === 'schedule')),
  )

export const evaluateChecklist = (
  input: CampaignChecklistInput,
): { checks: CampaignCheck[]; ready: boolean } => {
  const checks = buildCampaignChecks(input)
  return { checks, ready: checksPass(checks) }
}

const COUPON_TOKEN_RE = /\{\{\s*coupon(_code)?\s*\}\}/

// Whether any of these strings (subject, preview text, serialised blocks)
// uses the {{coupon}} token (or its legacy alias {{coupon_code}}).
export const usesCouponToken = (...texts: string[]): boolean =>
  texts.some((t) => COUPON_TOKEN_RE.test(t))

// Drawer / list "Send now", "Schedule…" and flow "Set live" act on the STORED
// record without opening the builder: a block email must already have its
// compiled HTML, a subject and blocks. Layout (classic) records pass.
export const storedRecordSendable = (record: {
  content_mode?: string | null
  compiled_html?: string | null
  subject?: string | null
  blocks?: unknown[] | null
  // Stamped at save time by the builder (see useCampaignDraft.emailContent):
  // the compiled_html contains a product section that resolved to no
  // products, so sending it would silently drop that section. Absent on
  // records saved before the flag existed — those stay sendable.
  has_empty_product_blocks?: boolean | null
}): { ok: boolean; reason?: string } => {
  if (record.content_mode !== 'blocks') return { ok: true }
  if (!String(record.subject ?? '').trim())
    return { ok: false, reason: 'subject' }
  if (!Array.isArray(record.blocks) || record.blocks.length === 0)
    return { ok: false, reason: 'blocks' }
  if (!String(record.compiled_html ?? '').trim())
    return { ok: false, reason: 'compiled_html' }
  if (record.has_empty_product_blocks)
    return { ok: false, reason: 'empty_product_blocks' }
  return { ok: true }
}

export const NOT_SENDABLE_MESSAGE =
  'Open this campaign in the builder to finish it before sending'

// storedRecordSendable reason 'empty_product_blocks' — the record is complete
// but one of its product sections would send empty.
export const EMPTY_PRODUCTS_NOT_SENDABLE_MESSAGE =
  'A product block in this email has no products to show — open it in the builder to fix it before sending'
